import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Trash2, Upload, Plus, Pencil, X, Check, GripVertical, Star } from "lucide-react";
import { STORE_CATALOG, CATALOG_BY_SLUG } from "../lib/storeCatalog";
import InventoryPanel from "./InventoryPanel";
import { OrdersPanel, AbandonedCartsPanel } from "./OrdersPanels";

// Resolves backend-relative URLs (from Emergent Object Storage /api/files/...) into full URLs.
const backend = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${backend}${u}` : u);

// Product categories — displayed as tabs on /seasonings New Drops section.
export const PRODUCT_CATEGORIES = ["Rub", "Sauce", "Bundle", "Apparel"];

const inputCls = "w-full border border-ink bg-white px-3 py-2 font-serif text-sm";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70";

// Size options matched to the hardcoded storefront (Seasonings & Merch page).
// Keep in sync with /app/frontend/src/pages/Seasonings.jsx rubs/sauces/bundles/merch.
const SIZE_OPTIONS = [
  {
    group: "Rubs",
    values: [
      "12 oz (340 g)",
      "12.1 oz (343 g)",
      "12.2 oz (345 g)",
      "12.3 oz (348 g)",
      "16 oz",
    ],
  },
  {
    group: "Sauces",
    values: [
      "20 oz (567 g)",
      "21 oz (595 g)",
      "21.5 oz (610 g)",
    ],
  },
  {
    group: "Bundles / Gift Packs",
    values: [
      "3 × 16 oz",
      "4 rubs · gift-ready",
    ],
  },
  {
    group: "Apparel",
    values: [
      "One size",
      "S",
      "M",
      "L",
      "XL",
      "2XL",
      "3XL",
    ],
  },
];

function SizeSelect({ value, onChange, testId }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={inputCls + " mt-1 cursor-pointer"}
    >
      <option value="">— Pick a size —</option>
      {SIZE_OPTIONS.map((g) => (
        <optgroup key={g.group} label={g.group}>
          {g.values.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/owner/upload-image", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url;
}

function ProductEditor({ product, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name: product.name || "",
    desc: product.desc || "",
    price: String(product.price ?? ""),
    size: product.size || "",
    tag: product.tag || "",
    category: product.category || "",
    image_url: product.image_url || "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("New image ready — click Save to apply");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.image_url) {
      toast.error("Name, price, and image are required");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/owner/products/${product.id}`, {
        ...form,
        price: parseFloat(form.price),
      });
      toast.success("Product updated");
      onSaved();
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-2 border-ink bg-white p-4" data-testid={`admin-prod-edit-${product.id}`}>
      <div className="flex items-center justify-between border-b border-ink pb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em]">Editing · {product.name}</span>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 border border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone" data-testid={`admin-prod-edit-cancel-${product.id}`}>
          <X size={12} /> Cancel
        </button>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="block"><span className={labelCls}>Name *</span><input data-testid={`admin-prod-edit-name-${product.id}`} className={inputCls + " mt-1"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="block"><span className={labelCls}>Price (USD) *</span><input data-testid={`admin-prod-edit-price-${product.id}`} type="number" step="0.01" className={inputCls + " mt-1"} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></label>
        <label className="block md:col-span-2"><span className={labelCls}>Description</span><textarea rows={2} data-testid={`admin-prod-edit-desc-${product.id}`} className={inputCls + " mt-1"} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} /></label>
        <label className="block"><span className={labelCls}>Size / Variant</span><SizeSelect value={form.size} onChange={(v) => setForm({ ...form, size: v })} testId={`admin-prod-edit-size-${product.id}`} /></label>
        <label className="block"><span className={labelCls}>Badge Tag</span><input data-testid={`admin-prod-edit-tag-${product.id}`} className={inputCls + " mt-1"} value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} /></label>
        <label className="block md:col-span-2"><span className={labelCls}>Category</span>
          <select
            data-testid={`admin-prod-edit-category-${product.id}`}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            className={inputCls + " mt-1 cursor-pointer"}
          >
            <option value="">— Pick a category —</option>
            {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <div className="md:col-span-2">
          <span className={labelCls}>Product Image *</span>
          <div className="mt-1 flex items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 border-2 border-ink bg-ink px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
              <Upload size={12} /> {uploading ? "Uploading…" : "Replace Image"}
              <input type="file" accept="image/*" onChange={onImage} className="hidden" data-testid={`admin-prod-edit-image-${product.id}`} />
            </label>
            {form.image_url && <img src={resolveUrl(form.image_url)} alt="preview" className="h-14 w-14 border border-ink object-cover" />}
          </div>
        </div>
      </div>
      <button type="button" onClick={save} disabled={saving} data-testid={`admin-prod-edit-save-${product.id}`} className="mt-4 inline-flex items-center gap-2 border-2 border-ink bg-ink px-6 py-2 font-alfa text-sm uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink disabled:opacity-50">
        <Check size={14} /> {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}

export function AdminStoreTab() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", desc: "", price: "", size: "", tag: "", category: "", image_url: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/products"); setProducts(data || []); setDirty(false); }
    catch { toast.error("Couldn't load products"); }
  };
  useEffect(() => { load(); }, []);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.image_url) { toast.error("Name, price, and image are required"); return; }
    setSaving(true);
    try {
      await api.post("/owner/products", { ...form, price: parseFloat(form.price) });
      setForm({ name: "", desc: "", price: "", size: "", tag: "", category: "", image_url: "" });
      toast.success("Product added! Live on the store now.");
      load();
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/owner/products/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (_e) { /* ignore */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    setProducts((cur) => {
      const src = cur.findIndex((p) => p.id === dragId);
      const dst = cur.findIndex((p) => p.id === targetId);
      if (src < 0 || dst < 0) return cur;
      const next = [...cur];
      const [moved] = next.splice(src, 1);
      next.splice(dst, 0, moved);
      return next;
    });
    setDirty(true);
    setDragId(null);
  };
  const moveBy = (id, delta) => {
    setProducts((cur) => {
      const i = cur.findIndex((p) => p.id === id);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      const [m] = next.splice(i, 1);
      next.splice(j, 0, m);
      return next;
    });
    setDirty(true);
  };
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.post("/owner/products/reorder", { ids: products.map((p) => p.id) });
      toast.success("Order saved — storefront updated");
      setDirty(false);
    } catch { toast.error("Couldn't save order"); }
    finally { setSavingOrder(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-store-tab">
      <OrdersPanel />
      <AbandonedCartsPanel />
      <div className="border-2 border-ink bg-bone p-6">
        <h3 className="font-alfa text-2xl uppercase">Add New Product</h3>
        <p className="mt-1 font-serif text-xs text-ink/60">Live PayPal checkout uses your existing merch account. Images upload to Emergent Object Storage.</p>
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <label className="block"><span className={labelCls}>Name *</span><input data-testid="admin-prod-name" className={inputCls + " mt-1"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label className="block"><span className={labelCls}>Price (USD) *</span><input data-testid="admin-prod-price" type="number" step="0.01" className={inputCls + " mt-1"} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></label>
          <label className="block md:col-span-2"><span className={labelCls}>Description</span><textarea rows={2} data-testid="admin-prod-desc" className={inputCls + " mt-1"} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} /></label>
          <label className="block"><span className={labelCls}>Size / Variant</span><SizeSelect value={form.size} onChange={(v) => setForm({ ...form, size: v })} testId="admin-prod-size" /></label>
          <label className="block"><span className={labelCls}>Badge Tag</span><input data-testid="admin-prod-tag" className={inputCls + " mt-1"} value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder='e.g., "New" or "Bundle"' /></label>
          <label className="block md:col-span-2"><span className={labelCls}>Category</span>
            <select
              data-testid="admin-prod-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className={inputCls + " mt-1 cursor-pointer"}
            >
              <option value="">— Pick a category —</option>
              {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div className="md:col-span-2">
            <span className={labelCls}>Product Image *</span>
            <div className="mt-1 flex items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 border-2 border-ink bg-ink px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-bone">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload Image"}
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="admin-prod-upload" />
              </label>
              {form.image_url && <img src={resolveUrl(form.image_url)} alt="preview" className="h-16 w-16 border border-ink object-cover" />}
            </div>
          </div>
        </div>
        <button type="button" onClick={save} disabled={saving} data-testid="admin-prod-save" className="mt-6 inline-flex items-center gap-2 border-2 border-ink bg-ink px-8 py-3 font-alfa text-base uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink disabled:opacity-50">
          <Plus size={16} /> {saving ? "Saving…" : "Publish to Store"}
        </button>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-alfa text-xl uppercase">Admin-Added Products ({products.length})</h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Drag tiles or use ▲ ▼ to reorder — the leftmost tile leads the New Drops row on Seasonings.</p>
          </div>
          {dirty && (
            <button
              type="button"
              onClick={saveOrder}
              disabled={savingOrder}
              data-testid="admin-prod-save-order"
              className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-5 py-2 font-alfa text-sm uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink disabled:opacity-50"
            >
              <Check size={14} /> {savingOrder ? "Saving…" : "Save Order"}
            </button>
          )}
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {products.map((p, idx) => (
            <div
              key={p.id}
              draggable={editingId !== p.id}
              onDragStart={onDragStart(p.id)}
              onDragOver={onDragOver}
              onDrop={onDrop(p.id)}
              onDragEnd={() => setDragId(null)}
              className={`border border-ink bg-white p-4 ${dragId === p.id ? "opacity-40" : ""}`}
              data-testid={`admin-prod-${p.id}`}
            >
              {editingId === p.id ? (
                <ProductEditor
                  product={p}
                  onSaved={() => { setEditingId(null); load(); }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                      <GripVertical size={12} /> #{idx + 1}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveBy(p.id, -1)}
                        disabled={idx === 0}
                        data-testid={`admin-prod-up-${p.id}`}
                        className="border border-ink/40 px-2 py-0.5 font-mono text-[10px] disabled:opacity-30 hover:bg-ink hover:text-bone"
                        aria-label="Move up"
                      >▲</button>
                      <button
                        type="button"
                        onClick={() => moveBy(p.id, 1)}
                        disabled={idx === products.length - 1}
                        data-testid={`admin-prod-down-${p.id}`}
                        className="border border-ink/40 px-2 py-0.5 font-mono text-[10px] disabled:opacity-30 hover:bg-ink hover:text-bone"
                        aria-label="Move down"
                      >▼</button>
                    </div>
                  </div>
                  <img src={resolveUrl(p.image_url)} alt={p.name} className="mb-3 aspect-[4/3] w-full cursor-move border border-ink object-cover" />
                  <p className="font-alfa text-lg uppercase leading-tight">{p.name}</p>
                  <p className="mt-1 font-mono text-sm font-bold">${p.price.toFixed(2)}</p>
                  {p.size && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">{p.size}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {p.category && (
                      <span className="inline-block border border-ink bg-ink px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-bone" data-testid={`admin-prod-cat-${p.id}`}>
                        {p.category}
                      </span>
                    )}
                    {p.tag && <span className="inline-block border border-ink px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em]">{p.tag}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button onClick={() => setEditingId(p.id)} className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone" data-testid={`admin-prod-edit-btn-${p.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await api.patch(`/owner/products/${p.id}`, { featured: !p.featured });
                          toast.success(p.featured ? "Removed from spotlight" : "Set as spotlight product");
                          load();
                        } catch { toast.error("Couldn't update"); }
                      }}
                      className={`inline-flex items-center gap-1 border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${p.featured ? "border-red-700 bg-red-700 text-bone" : "border-ink hover:bg-ink hover:text-bone"}`}
                      data-testid={`admin-prod-feature-${p.id}`}
                    >
                      <Star size={12} className={p.featured ? "fill-current" : ""} /> {p.featured ? "Featured" : "Feature"}
                    </button>
                    <button onClick={() => remove(p.id, p.name)} className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-red-700 hover:bg-red-700 hover:text-bone" data-testid={`admin-prod-delete-${p.id}`}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
          {products.length === 0 && <p className="col-span-full font-mono text-xs text-ink/50">No admin products yet — add one above.</p>}
        </div>
      </div>

      <PromoteStorefrontPanel />
      <StoreImageOverridesPanel />
      <InventoryPanel />
    </div>
  );
}

function StoreImageOverridesPanel() {
  const [overrides, setOverrides] = useState({}); // { slug: { name?, desc?, price?, size?, tag?, image_url?, hidden? } }
  const [uploading, setUploading] = useState(null); // slug currently uploading
  const [editingSlug, setEditingSlug] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/store-image-overrides"); setOverrides(data || {}); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const uploadReplacement = (slug) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(slug);
    try {
      const url = await uploadFile(file);
      await api.post("/owner/store-image-overrides", { slug, image_url: url });
      toast.success("Image replaced — live on /seasonings");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(null); e.target.value = ""; }
  };

  const reset = async (slug) => {
    if (!window.confirm("Reset this item — clear ALL your edits and bring back the original name, price, and image?")) return;
    try {
      await api.delete(`/owner/store-image-overrides/${encodeURIComponent(slug)}`);
      toast.success("Reset to original");
      load();
    } catch { toast.error("Couldn't reset"); }
  };

  const toggleHide = async (slug, currentlyHidden) => {
    try {
      await api.post("/owner/store-image-overrides", { slug, hidden: !currentlyHidden });
      toast.success(currentlyHidden ? "Item is visible again" : "Item hidden from the storefront");
      load();
    } catch { toast.error("Couldn't update"); }
  };

  return (
    <div data-testid="store-image-overrides-panel">
      <h3 className="font-alfa text-xl uppercase">Edit Storefront Items</h3>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
        Change the name, description, price, size, tag or photo of any built-in item. Or hide it entirely so it's gone from /seasonings. Reset brings the original back.
      </p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
        Want to <b>add a brand-new item?</b> Use the <b>Add New Product</b> form at the top of this tab — it'll show in the New Drops row on /seasonings.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {STORE_CATALOG.map((item) => {
          const ov = overrides[item.slug] || {};
          const isHidden = ov.hidden === true;
          const hasEdits = Object.keys(ov).some((k) => k !== "hidden");
          const displaySrc = ov.image_url ? resolveUrl(ov.image_url) : item.img;
          const displayName = ov.name || item.name;
          const displayPrice = typeof ov.price === "number" ? ov.price : item.price;
          const isUploading = uploading === item.slug;
          const isEditing = editingSlug === item.slug;
          return (
            <div key={item.slug} className={`flex flex-col gap-2 border bg-white p-3 ${isHidden ? "border-red-700 opacity-70" : "border-ink"}`} data-testid={`override-${item.slug}`}>
              <div className="flex items-center gap-3">
                <div className="relative h-16 w-16 flex-shrink-0 border border-ink/40 bg-white">
                  <img src={displaySrc} alt={displayName} className="h-full w-full object-contain" />
                  {(ov.image_url || hasEdits) && (
                    <span className="absolute -right-1 -top-1 border border-bone bg-red-700 px-1 font-mono text-[9px] uppercase tracking-[0.15em] text-bone">
                      Edited
                    </span>
                  )}
                  {isHidden && (
                    <span className="absolute inset-0 flex items-center justify-center bg-ink/70 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
                      Hidden
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-alfa text-sm uppercase leading-tight">{displayName}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">{item.category} · ${displayPrice.toFixed(2)}</p>
                </div>
              </div>

              {isEditing && (
                <OverrideEditor
                  item={item}
                  currentOverride={ov}
                  onCancel={() => setEditingSlug(null)}
                  onSaved={() => { setEditingSlug(null); load(); }}
                />
              )}

              {!isEditing && (
                <div className="flex flex-wrap items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditingSlug(item.slug)}
                    data-testid={`override-edit-${item.slug}`}
                    className="inline-flex items-center gap-1 border border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                  <label className="inline-flex cursor-pointer items-center gap-1 border border-ink bg-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink">
                    <Upload size={12} /> {isUploading ? "…" : (ov.image_url ? "Replace" : "Photo")}
                    <input type="file" accept="image/*" onChange={uploadReplacement(item.slug)} className="hidden" data-testid={`override-upload-${item.slug}`} disabled={isUploading} />
                  </label>
                  <button
                    type="button"
                    onClick={() => toggleHide(item.slug, isHidden)}
                    data-testid={`override-hide-${item.slug}`}
                    className={`inline-flex items-center gap-1 border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${isHidden ? "border-red-700 bg-red-700 text-bone" : "border-ink hover:bg-ink hover:text-bone"}`}
                  >
                    {isHidden ? <><Plus size={12} /> Show</> : <><X size={12} /> Hide</>}
                  </button>
                  {(hasEdits || isHidden) && (
                    <button
                      type="button"
                      onClick={() => reset(item.slug)}
                      data-testid={`override-reset-${item.slug}`}
                      className="inline-flex items-center gap-1 border border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-red-700 hover:bg-red-700 hover:text-bone"
                    >
                      <Trash2 size={12} /> Reset
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OverrideEditor({ item, currentOverride, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: currentOverride.name ?? item.name,
    desc: currentOverride.desc ?? item.desc,
    price: String(typeof currentOverride.price === "number" ? currentOverride.price : item.price),
    size: currentOverride.size ?? item.size ?? "",
    tag: currentOverride.tag ?? item.tag ?? "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const payload = { slug: item.slug };
    // Only send fields that differ from the built-in defaults so unchanged fields fall back to originals.
    if (form.name.trim() && form.name !== item.name) payload.name = form.name.trim();
    if ((form.desc || "").trim() && form.desc !== item.desc) payload.desc = form.desc.trim();
    const numericPrice = parseFloat(form.price);
    if (!Number.isNaN(numericPrice) && numericPrice !== item.price) payload.price = numericPrice;
    if ((form.size || "").trim() && form.size !== (item.size || "")) payload.size = form.size.trim();
    if ((form.tag || "").trim() && form.tag !== (item.tag || "")) payload.tag = form.tag.trim();
    if (Object.keys(payload).length === 1) {
      toast.info("Nothing to save — every field matches the original");
      onCancel();
      return;
    }
    setSaving(true);
    try {
      await api.post("/owner/store-image-overrides", payload);
      toast.success("Saved — live on /seasonings");
      onSaved();
    } catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-2 border-ink bg-bone p-3" data-testid={`override-editor-${item.slug}`}>
      <label className="block"><span className={labelCls}>Name</span>
        <input data-testid={`override-name-${item.slug}`} className={inputCls + " mt-1"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <label className="mt-2 block"><span className={labelCls}>Description</span>
        <textarea rows={2} data-testid={`override-desc-${item.slug}`} className={inputCls + " mt-1"} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} />
      </label>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="block"><span className={labelCls}>Price</span>
          <input data-testid={`override-price-${item.slug}`} type="number" step="0.01" className={inputCls + " mt-1"} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
        </label>
        <label className="block"><span className={labelCls}>Tag</span>
          <input data-testid={`override-tag-${item.slug}`} className={inputCls + " mt-1"} value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
        </label>
      </div>
      <label className="mt-2 block"><span className={labelCls}>Size / Variant</span>
        <input data-testid={`override-size-${item.slug}`} className={inputCls + " mt-1"} value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
      </label>
      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={save} disabled={saving} data-testid={`override-save-${item.slug}`} className="inline-flex items-center gap-1 border-2 border-ink bg-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink disabled:opacity-50">
          <Check size={12} /> {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1 border border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}

function PromoteStorefrontPanel() {
  const [promoted, setPromoted] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/promoted-items"); setPromoted(data || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const promotedSlugs = new Set(promoted.map((p) => p.slug));

  const add = async (slug) => {
    try { await api.post("/owner/promoted-items", { slug }); toast.success("Added to New Drops"); load(); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't add");
    }
  };
  const remove = async (slug) => {
    try { await api.delete(`/owner/promoted-items/${encodeURIComponent(slug)}`); toast.success("Removed from New Drops"); load(); }
    catch { toast.error("Couldn't remove"); }
  };

  return (
    <div data-testid="promote-storefront-panel">
      <h3 className="font-alfa text-xl uppercase">Slide Storefront Items Into New Drops</h3>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
        Pick any existing rub, sauce, bundle or apparel to feature in the New Drops row. Currently promoted: <b>{promoted.length}</b>.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {STORE_CATALOG.map((item) => {
          const isOn = promotedSlugs.has(item.slug);
          return (
            <div key={item.slug} className={`flex items-center gap-3 border p-3 ${isOn ? "border-ink bg-ink text-bone" : "border-ink/40 bg-white"}`} data-testid={`catalog-${item.slug}`}>
              <img src={item.img} alt={item.name} className="h-14 w-14 border border-ink/40 object-contain bg-white" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-alfa text-sm uppercase leading-tight">{item.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">{item.category} · ${item.price.toFixed(2)}</p>
              </div>
              {isOn ? (
                <button type="button" onClick={() => remove(item.slug)} className="inline-flex items-center gap-1 border border-bone px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-bone hover:text-ink" data-testid={`catalog-remove-${item.slug}`}>
                  <X size={12} /> Remove
                </button>
              ) : (
                <button type="button" onClick={() => add(item.slug)} className="inline-flex items-center gap-1 border border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bone" data-testid={`catalog-add-${item.slug}`}>
                  <Plus size={12} /> Add
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminGalleryTab() {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/gallery-photos"); setPhotos(data || []); setDirty(false); }
    catch { toast.error("Couldn't load photos"); }
  };
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      await api.post("/owner/gallery-photos", { image_url: url, caption });
      setCaption("");
      toast.success("Photo added to gallery");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/owner/gallery-photos/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (_e) { /* ignore */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) return;
    setPhotos((cur) => {
      const src = cur.findIndex((p) => p.id === dragId);
      const dst = cur.findIndex((p) => p.id === targetId);
      if (src < 0 || dst < 0) return cur;
      const next = [...cur];
      const [moved] = next.splice(src, 1);
      next.splice(dst, 0, moved);
      return next;
    });
    setDirty(true);
    setDragId(null);
  };

  const moveBy = (id, delta) => {
    setPhotos((cur) => {
      const i = cur.findIndex((p) => p.id === id);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      const [m] = next.splice(i, 1);
      next.splice(j, 0, m);
      return next;
    });
    setDirty(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.post("/owner/gallery-photos/reorder", { ids: photos.map((p) => p.id) });
      toast.success("Order saved — gallery updated");
      setDirty(false);
    } catch { toast.error("Couldn't save order"); }
    finally { setSavingOrder(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-gallery-tab">
      <div className="border-2 border-ink bg-bone p-6">
        <h3 className="font-alfa text-2xl uppercase">Add Gallery Photo</h3>
        <p className="mt-1 font-serif text-xs text-ink/60">New photos appear on top of the Gallery page. Curated hardcoded photos stay below. Drag to reorder.</p>
        <div className="mt-4">
          <label className="mb-3 block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/70">Caption (optional)</span>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1 w-full border border-ink bg-white px-3 py-2 font-serif text-sm" data-testid="admin-gallery-caption" />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 border-2 border-ink bg-ink px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-bone">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload Photo"}
            <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="admin-gallery-upload" />
          </label>
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-alfa text-xl uppercase">Uploaded Photos ({photos.length})</h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Drag any tile — or use ▲ ▼ — to change the order. Save when done.</p>
          </div>
          {dirty && (
            <button
              type="button"
              onClick={saveOrder}
              disabled={savingOrder}
              data-testid="admin-gallery-save-order"
              className="inline-flex items-center gap-2 border-2 border-ink bg-ink px-5 py-2 font-alfa text-sm uppercase tracking-[0.2em] text-bone hover:bg-transparent hover:text-ink disabled:opacity-50"
            >
              <Check size={14} /> {savingOrder ? "Saving…" : "Save Order"}
            </button>
          )}
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {photos.map((p, idx) => (
            <div
              key={p.id}
              draggable
              onDragStart={onDragStart(p.id)}
              onDragOver={onDragOver}
              onDrop={onDrop(p.id)}
              onDragEnd={() => setDragId(null)}
              className={`border border-ink bg-white ${dragId === p.id ? "opacity-40" : ""}`}
              data-testid={`admin-gallery-${p.id}`}
            >
              <div className="flex items-center justify-between border-b border-ink/20 bg-bone px-2 py-1">
                <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">
                  <GripVertical size={12} /> #{idx + 1}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveBy(p.id, -1)}
                    disabled={idx === 0}
                    data-testid={`admin-gallery-up-${p.id}`}
                    className="border border-ink/40 px-2 py-0.5 font-mono text-[10px] disabled:opacity-30 hover:bg-ink hover:text-bone"
                    aria-label="Move up"
                  >▲</button>
                  <button
                    type="button"
                    onClick={() => moveBy(p.id, 1)}
                    disabled={idx === photos.length - 1}
                    data-testid={`admin-gallery-down-${p.id}`}
                    className="border border-ink/40 px-2 py-0.5 font-mono text-[10px] disabled:opacity-30 hover:bg-ink hover:text-bone"
                    aria-label="Move down"
                  >▼</button>
                </div>
              </div>
              <img src={resolveUrl(p.image_url)} alt={p.caption || "gallery"} className="aspect-square w-full cursor-move object-cover" />
              {p.caption && <p className="border-t border-ink/20 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]">{p.caption}</p>}
              <button onClick={() => remove(p.id)} className="w-full border-t border-ink px-2 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-red-700 hover:text-bone">
                <Trash2 size={12} className="inline" /> Delete
              </button>
            </div>
          ))}
          {photos.length === 0 && <p className="col-span-full font-mono text-xs text-ink/50">No uploaded photos yet.</p>}
        </div>
      </div>
    </div>
  );
}
